import { ThemeProvider } from '@mui/material'
import {
  add,
  endOfMonth,
  getMonth,
  getUnixTime,
  setMonth,
  startOfMonth
} from 'date-fns'
import { GetServerSideProps } from 'next'
import { useRouter } from 'next/router'
import { SessionProvider } from 'next-auth/react'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { renderToString } from 'react-dom/server'
import { getServerState, InstantSearchServerState } from 'react-instantsearch'
import * as yup from 'yup'
import Layout from 'components/Layout'
import { searchIndex } from 'containers/meilisearchClient'
import SeoPages from 'containers/seoPages'
import Meta from 'containers/seoPages/Meta'
import {
  getBaseUrl,
  getParsedData,
  queryLocationSchema,
  queryMonthSchema,
  queryTypeOrDistanceSchema
} from 'helpers/index'
import { liveRateRepository } from 'live/liveInstances'
import i18nConfig from 'next-i18next.config'
import { theme } from 'styles/theme'
import { defaultFrontLocales } from 'utils/locale'

const querySchema = yup
  .object()
  .shape({
    page: yup.number()
  })
  .concat(queryLocationSchema)
  .concat(queryTypeOrDistanceSchema)
  .concat(queryMonthSchema)

interface PageProps {
  attributesToRetrieve: string[]
  serverState?: InstantSearchServerState
  filters: string
  typeOrDistanceKey: string | null
  parsedLocation?: string
  parsedMonth?: number
  nextMonthTS: number
  currencyExchangeRates: Record<string, number>
}

function Page({
  filters,
  attributesToRetrieve,
  typeOrDistanceKey,
  parsedLocation,
  parsedMonth,
  nextMonthTS,
  serverState,
  currencyExchangeRates
}: PageProps) {
  const router = useRouter()
  const { query } = router

  const { typeOrDistance, page, location, month } =
    querySchema.validateSync(query)

  return (
    <>
      <Meta
        typeOrDistanceKey={typeOrDistanceKey}
        parsedLocation={parsedLocation}
        parsedMonth={parsedMonth}
        nextMonthTS={nextMonthTS}
        serverState={serverState}
      />
      <Layout>
        <SeoPages
          attributesToRetrieve={attributesToRetrieve}
          serverState={serverState}
          filters={filters}
          page={page}
          typeOrDistance={typeOrDistance}
          location={location}
          month={month}
          currencyExchangeRates={currencyExchangeRates}
        />
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<PageProps> = async ({
  locale,
  query,
  res
}) => {
  const isValid = querySchema.isValidSync(query)
  if (!isValid) {
    return {
      notFound: true
    }
  }

  const { typeOrDistance, page, location, month } = query

  const {
    parsedLocation,
    typeOrDistanceKey,
    parsedMonth,
    typeOrDistanceAlgoliaTranslation
  } = getParsedData({
    location,
    typeOrDistance,
    month
  })
  const today = getUnixTime(Date.now() * 1000)
  const distanceFilter = typeOrDistanceAlgoliaTranslation
    ? `(editionLiveLevel2CategoryTranslation="${typeOrDistanceAlgoliaTranslation}" OR editionLiveLevel1CategoryTranslation="${typeOrDistanceAlgoliaTranslation}") AND`
    : ''
  const locationFilter = parsedLocation
    ? `eventLocationData="${parsedLocation}" AND`
    : ''

  if (typeof parsedMonth !== 'number') {
    return {
      notFound: true
    }
  }

  const currentMonth = getMonth(today)
  const date =
    parsedMonth < currentMonth
      ? add(today, { years: 1 })
      : setMonth(today, parsedMonth!)
  const nextMonthDate = startOfMonth(setMonth(date, parsedMonth!))
  const nextMonthStartTS = getUnixTime(nextMonthDate)
  const nextMonthEndTS = getUnixTime(endOfMonth(nextMonthDate))
  const timeFilter = `editionLiveStartDateTimestamp ${nextMonthStartTS} TO ${nextMonthEndTS} OR editionLiveEndDateTimestamp ${nextMonthStartTS} TO ${nextMonthEndTS}`

  const filters = !typeOrDistance
    ? `${locationFilter} (${timeFilter}) AND eventStatus="LIVE"`
    : `${distanceFilter} ${locationFilter} (${timeFilter}) AND eventStatus="LIVE"`

  const currencyExchangeRates =
    await liveRateRepository.getRunnerRatesForDefaultCurrency()

  if (page === 1) {
    return {
      redirect: {
        destination: `${getBaseUrl(
          locale
        )}/${typeOrDistance}/${location}/${month}`,
        permanent: true
      }
    }
  }

  const props = {
    ...(await serverSideTranslations(
      locale as string,
      [...defaultFrontLocales, 'event'],
      i18nConfig
    )),
    filters,
    typeOrDistanceKey: typeOrDistanceKey ?? null,
    parsedLocation,
    parsedMonth,
    nextMonthTS: nextMonthStartTS,
    currencyExchangeRates,
    attributesToRetrieve: [
      'eventName',
      'eventCity',
      'eventLiveDistanceKm',
      'eventCountrySubdivisionDisplayCodeLevel2',
      'editionLiveLevel1CategoryKey',
      'eventSlug',
      'editionCalendarStatus',
      'eventLivePriceStartingFrom',
      'editionLiveStartDateTimestamp',
      'editionLiveEndDateTimestamp',
      'eventCoverImage',
      'editionLiveLevel2CategoryKey',
      'eventIsFeatured',
      'objectID'
    ]
  }

  const serverState = await getServerState(
    <ThemeProvider theme={theme}>
      <SessionProvider>
        <SeoPages {...props} />
      </SessionProvider>
    </ThemeProvider>,
    {
      renderToString
    }
  )

  // Since getStaticProps cannot handle queryParams, we still need to use getServerSideProps here
  // but with cache enabled
  res.setHeader('Cache-Control', 's-maxage=86400, stale-while-revalidate=3600')

  const initialResults = serverState.initialResults[searchIndex]?.results?.[0]
  if (initialResults?.nbHits === 0) {
    return {
      redirect: {
        destination: `${getBaseUrl(locale)}/${typeOrDistance}/${location}`,
        permanent: true
      }
    }
  }
  if (initialResults && page && page > initialResults.nbPages) {
    return {
      redirect: {
        destination: `${getBaseUrl(
          locale
        )}/${typeOrDistance}/${location}/${month}`,
        permanent: true
      }
    }
  }

  return {
    props: {
      ...props,
      serverState
    }
  }
}
export default Page
